import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    ChatBubbleLeftRightIcon,
    PencilSquareIcon,
    ArrowLeftIcon,
    BriefcaseIcon,
    BuildingOffice2Icon,
    MapPinIcon,
} from '@heroicons/react/24/outline';
import { CollaboratorAvatar } from '@/features/Collaborators/Shared';
import AvatarEditavel from './components/AvatarEditavel';
import { translateActivityStatus } from '@/utils/collaborator-status';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';

const getStatusMeta = (status?: string | null) => {
    const label = translateActivityStatus(status);
    const normalized = status?.toLowerCase();
    if (normalized === 'active') return { label, tone: 'bg-emerald-50 text-emerald-700 ring-emerald-100' };
    if (normalized === 'inactive' || normalized === 'desligado') return { label, tone: 'bg-rose-50 text-rose-700 ring-rose-100' };
    if (normalized === 'afastado') return { label, tone: 'bg-amber-50 text-amber-700 ring-amber-100' };
    return { label, tone: 'bg-slate-50 text-slate-700 ring-slate-100' };
};

interface ProfileHeaderProps {
    collaborator: any;
    departmentLabel?: string | null;
    location?: string;
    onBack: () => void;
    hideActions?: boolean;
    hideStatus?: boolean;
    /** Mostra "Trocar foto" ao clicar na foto (só na edição do perfil, ver CollaboratorsLayout). */
    photoEditable?: boolean;
}

export default function ProfileHeader({
    collaborator,
    departmentLabel: initialDepartmentLabel,
    location,
    onBack,
    hideActions = false,
    hideStatus = false,
    photoEditable = false
}: ProfileHeaderProps) {
    const [fetchedDepartmentLabel, setFetchedDepartmentLabel] = useState<string | null>(null);

    useEffect(() => {
        if (initialDepartmentLabel || !collaborator?.departmentId) return;

        let cancelled = false;
        const fetchDepartment = async () => {
            try {
                const snap = await getDoc(doc(db, 'departments', collaborator.departmentId));
                if (!snap.exists()) return;
                const data = snap.data() as { completeDescription?: string; name?: string; title?: string };
                const resolved = data.completeDescription || data.name || data.title || null;
                if (!cancelled) {
                    setFetchedDepartmentLabel(resolved);
                }
            } catch (err) {
                console.error('[ProfileHeader] Failed to fetch department', err);
            }
        };

        fetchDepartment();
        return () => {
            cancelled = true;
        };
    }, [collaborator?.departmentId, initialDepartmentLabel]);

    if (!collaborator) return null;

    const statusMeta = getStatusMeta(collaborator.status);
    const editLink = collaborator?.id ? `/collaborators/${collaborator.id}/edit` : undefined;
    const chatLink = collaborator?.email
        ? `https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(collaborator.email)}`
        : undefined;

    // Fallback for location if not provided
    const displayLocation = location || (collaborator.addresses?.[0]
        ? [collaborator.addresses[0].city, collaborator.addresses[0].state].filter(Boolean).join(' - ')
        : [collaborator.city, collaborator.state].filter(Boolean).join(' - '));

    // Fallback for department if not provided
    const displayDepartment = initialDepartmentLabel || fetchedDepartmentLabel || collaborator.departmentName || 'Departamento não informado';

    return (
        <div className="mb-6 overflow-hidden rounded-3xl bg-white shadow-lg ring-1 ring-slate-100">
            <div className="h-32 bg-gradient-to-r from-indigo-500 to-purple-600 relative">
                <button
                    onClick={onBack}
                    className="absolute left-6 top-6 rounded-full bg-white/20 p-2 text-white backdrop-blur-sm transition hover:bg-white/30"
                    aria-label="Voltar"
                >
                    <ArrowLeftIcon className="h-5 w-5" />
                </button>
            </div>
            <div className="relative px-6 pb-6">
                <div className="-mt-12 mb-4 flex justify-between items-end">
                    <div className="rounded-full bg-white p-1.5 ring-1 ring-slate-100 shadow-sm">
                        {photoEditable && collaborator.id ? (
                            <AvatarEditavel
                                collaboratorId={collaborator.id}
                                name={collaborator.name}
                                photoUrl={collaborator.photoUrl}
                            />
                        ) : (
                            <CollaboratorAvatar
                                name={collaborator.name}
                                photoUrl={collaborator.photoUrl}
                                size="xlarge"
                            />
                        )}
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                        {!hideActions && chatLink && (
                            <a
                                href={chatLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-indigo-50 text-indigo-700 transition hover:bg-indigo-100 hover:text-indigo-900"
                                title="Chat no Teams"
                            >
                                <ChatBubbleLeftRightIcon className="h-5 w-5" />
                            </a>
                        )}
                        {!hideActions && editLink && (
                            <Link
                                to={editLink}
                                className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-indigo-50 text-indigo-700 transition hover:bg-indigo-100 hover:text-indigo-900"
                                title="Editar colaborador"
                            >
                                <PencilSquareIcon className="h-5 w-5" />
                            </Link>
                        )}
                        {!hideStatus && (
                            <span
                                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ring-1 ${statusMeta.tone} ml-2`}
                            >
                                <span className="h-1.5 w-1.5 rounded-full bg-current" />
                                {statusMeta.label}
                            </span>
                        )}
                    </div>
                </div>

                <div>
                    <h1 className="text-2xl font-bold text-slate-900">{collaborator.name}</h1>
                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-600">
                        <span className="flex items-center gap-1.5">
                            <BriefcaseIcon className="h-4 w-4 text-slate-400" />
                            {collaborator.positionName || 'Cargo não informado'}
                        </span>
                        <span className="flex items-center gap-1.5">
                            <BuildingOffice2Icon className="h-4 w-4 text-slate-400" />
                            {displayDepartment}
                        </span>
                        {displayLocation && (
                            <span className="flex items-center gap-1.5">
                                <MapPinIcon className="h-4 w-4 text-slate-400" />
                                {displayLocation}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
